// groundedness.ts — topic-agnostic grounding verification for the
// generative path. Three checks, all derived from retrieved context alone
// (no enumerated denylists, no hardcoded topics).
//
// Layer 1 — RETRIEVAL CONFIDENCE
//   "Is anything in the corpus even relevant?"
//   If top BM25 < ~2.0 AND top embedding cosine < ~0.45, the question is
//   off-corpus. Refuse before invoking the LLM.
//
// Layer 2 — SEMANTIC GROUNDING (post-LLM)
//   Split response into sentences. Embed each. Compare to embeddings of
//   retrieved chunks. If any sentence has max-cosine < threshold, the LLM
//   claimed something the context doesn't support. Flag.
//
// Layer 3 — ENTITY GROUNDING (post-LLM)
//   Extract proper-noun-shaped tokens + numbers from the response.
//   Each must appear (or fuzzy-match) in the context. Catches invented
//   names like "his wife Riya" where Riya isn't in any chunk.
//
// References:
//   - Faithfulness as defined in RAGAS (Es et al. 2023): per-claim NLI vs context
//   - Self-RAG (Asai et al. 2023): retrieval-aware refusal tokens
//   - TruLens "groundedness" metric: same per-claim approach
//
// We can't run NLI in browser, so we use semantic+entity proxies. They
// catch the same classes of hallucination at far lower cost.

import type { Chunk } from "@/data/corpus";
import { cosine, embed } from "@/lib/embedder";

// ───────────────────────────────────────────────────────────────
// Tunable thresholds, calibrated empirically. Move to top-of-file
// constants so we can sweep them later via the smoke-test suite.
// ───────────────────────────────────────────────────────────────
export const THRESHOLDS = {
  // Layer 1
  minBM25Score: 2.0,           // top BM25 must hit this …
  minEmbeddingCosine: 0.45,    // … OR top embedding cosine must hit this
  // Layer 2
  minSentenceMaxCosine: 0.42,  // each response sentence must hit this similarity to SOME chunk
  // Layer 3
  minEntityMatchRate: 0.6,     // ≥60% of response entities must appear in context
  // Sentences shorter than this don't need to be grounded (greetings, "yes", etc.)
  minSentenceLengthChars: 25,
};

// ───────────────────────────────────────────────────────────────
// Layer 1 — retrieval confidence
// ───────────────────────────────────────────────────────────────
export type RetrievalGate = {
  passed: boolean;
  topBM25: number;
  topEmbeddingCosine: number;
  reason: string;
};

export function retrievalGate(
  topBM25: number,
  topEmbeddingCosine: number,
): RetrievalGate {
  const bm25Ok = topBM25 >= THRESHOLDS.minBM25Score;
  const embOk = topEmbeddingCosine >= THRESHOLDS.minEmbeddingCosine;
  const passed = bm25Ok || embOk;
  const reason = passed
    ? `passed (bm25=${topBM25.toFixed(2)}${bm25Ok ? "≥" : "<"}${THRESHOLDS.minBM25Score}, cos=${topEmbeddingCosine.toFixed(2)}${embOk ? "≥" : "<"}${THRESHOLDS.minEmbeddingCosine})`
    : `nothing relevant in corpus (bm25=${topBM25.toFixed(2)}, cos=${topEmbeddingCosine.toFixed(2)})`;
  return { passed, topBM25, topEmbeddingCosine, reason };
}

// ───────────────────────────────────────────────────────────────
// Sentence splitting (simple; good enough for English responses)
// ───────────────────────────────────────────────────────────────
export function splitSentences(text: string): string[] {
  return text
    .replace(/\s+/g, " ")
    .trim()
    .split(/(?<=[.!?])\s+(?=[A-Z"'])/g)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

// ───────────────────────────────────────────────────────────────
// Layer 2 — semantic grounding
// ───────────────────────────────────────────────────────────────
export type SemanticGroundingReport = {
  passed: boolean;
  perSentence: Array<{ sentence: string; maxCosine: number; supportedBy: string | null }>;
  weakestSentence: string | null;
  weakestScore: number;
};

export async function semanticGrounding(
  response: string,
  chunks: Chunk[],
  chunkEmbeddings: Float32Array[],
): Promise<SemanticGroundingReport> {
  const sentences = splitSentences(response).filter(
    (s) => s.length >= THRESHOLDS.minSentenceLengthChars,
  );
  if (sentences.length === 0 || chunkEmbeddings.length === 0) {
    return {
      passed: true,
      perSentence: [],
      weakestSentence: null,
      weakestScore: 1,
    };
  }

  const perSentence: SemanticGroundingReport["perSentence"] = [];
  for (const sentence of sentences) {
    const emb = await embed(sentence);
    let maxCos = -1;
    let bestIdx = -1;
    for (let i = 0; i < chunkEmbeddings.length; i++) {
      const c = cosine(emb, chunkEmbeddings[i]);
      if (c > maxCos) {
        maxCos = c;
        bestIdx = i;
      }
    }
    perSentence.push({
      sentence,
      maxCosine: Math.round(maxCos * 1000) / 1000,
      supportedBy: bestIdx >= 0 ? chunks[bestIdx]?.id ?? null : null,
    });
  }

  const weakest = perSentence.reduce(
    (acc, s) => (s.maxCosine < acc.maxCosine ? s : acc),
    perSentence[0],
  );

  return {
    passed: weakest.maxCosine >= THRESHOLDS.minSentenceMaxCosine,
    perSentence,
    weakestSentence: weakest.sentence,
    weakestScore: weakest.maxCosine,
  };
}

// ───────────────────────────────────────────────────────────────
// Layer 3 — entity grounding
// ───────────────────────────────────────────────────────────────
//
// Extract proper-noun-shaped tokens (Capitalised words, multi-word
// CamelCase brand names, $amounts, large numbers, %s, years).
// Each must appear in the context (case-insensitive substring match
// with a small allow-list for common pronouns/glue words).
// ───────────────────────────────────────────────────────────────

const COMMON_CAPS = new Set([
  "I", "He", "She", "They", "We", "You", "It", "His", "Her", "Their",
  "The", "A", "An", "And", "But", "If", "Or", "So", "On", "In", "At",
  "Harsh", "Harsh's", "Joshi", "Joshi's", // these are always allowed
  "AI", "API", "URL", "URLs", "OK", "NYC", "SF", "UK", "US", "USA",
  "JoshLM", "JoshLM-7B", // brand
]);

export type EntityGroundingReport = {
  passed: boolean;
  totalEntities: number;
  matchedEntities: number;
  matchRate: number;
  unsupported: string[];
};

export function entityGrounding(
  response: string,
  chunks: Chunk[],
): EntityGroundingReport {
  const contextLower = chunks.map((c) => c.text.toLowerCase()).join(" ");

  // Capture: capitalised single tokens, CamelCase / hyphen-joined brands,
  // money amounts, percentages, large numbers, years.
  const tokens = new Set<string>();
  // Capitalised words and brand-like tokens
  const propRe = /\b(?:[A-Z][a-zA-Z0-9]+(?:[-./][A-Z][a-zA-Z0-9]+)*|[A-Z]{2,})\b/g;
  let m: RegExpExecArray | null;
  while ((m = propRe.exec(response))) {
    if (!COMMON_CAPS.has(m[0])) tokens.add(m[0]);
  }
  // Numbers, money, percentages, years
  const numRe = /\$?\d[\d,]*(?:\.\d+)?[KMB%+]?\b/g;
  while ((m = numRe.exec(response))) {
    tokens.add(m[0]);
  }

  const total = tokens.size;
  if (total === 0) {
    return {
      passed: true,
      totalEntities: 0,
      matchedEntities: 0,
      matchRate: 1,
      unsupported: [],
    };
  }

  const unsupported: string[] = [];
  let matched = 0;
  for (const tok of tokens) {
    if (contextLower.includes(tok.toLowerCase())) {
      matched++;
    } else {
      // Fuzzy fallback: strip trailing 's, punctuation; try again
      const stripped = tok.toLowerCase().replace(/'s$|\W+$/g, "");
      if (stripped && contextLower.includes(stripped)) {
        matched++;
      } else {
        unsupported.push(tok);
      }
    }
  }

  const rate = matched / total;
  return {
    passed: rate >= THRESHOLDS.minEntityMatchRate,
    totalEntities: total,
    matchedEntities: matched,
    matchRate: Math.round(rate * 100) / 100,
    unsupported,
  };
}

// ───────────────────────────────────────────────────────────────
// Combined report
// ───────────────────────────────────────────────────────────────
export type GroundednessReport = {
  retrieval: RetrievalGate;
  semantic: SemanticGroundingReport | null;
  entity: EntityGroundingReport | null;
  overall: "passed" | "failed-retrieval" | "failed-semantic" | "failed-entity";
};

export async function checkGroundedness(opts: {
  response: string;
  chunks: Chunk[];
  chunkEmbeddings: Float32Array[];
  topBM25Score: number;
  topEmbeddingCosine: number;
  skipPostChecks?: boolean;
}): Promise<GroundednessReport> {
  const retrieval = retrievalGate(opts.topBM25Score, opts.topEmbeddingCosine);
  if (!retrieval.passed) {
    return { retrieval, semantic: null, entity: null, overall: "failed-retrieval" };
  }
  if (opts.skipPostChecks) {
    return { retrieval, semantic: null, entity: null, overall: "passed" };
  }

  const semantic = await semanticGrounding(
    opts.response,
    opts.chunks,
    opts.chunkEmbeddings,
  );
  if (!semantic.passed) {
    const entity = entityGrounding(opts.response, opts.chunks);
    return { retrieval, semantic, entity, overall: "failed-semantic" };
  }

  const entity = entityGrounding(opts.response, opts.chunks);
  if (!entity.passed) {
    return { retrieval, semantic, entity, overall: "failed-entity" };
  }

  return { retrieval, semantic, entity, overall: "passed" };
}
